import { createPortal } from "react-dom";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Square } from "lucide-react";

export function ListboxField({ label, value, options, onChange, placeholder = "自动", disabled = false, showMeta = false }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [focusedValue, setFocusedValue] = useState(value);
  const [menuStyle, setMenuStyle] = useState(null);
  const optionRefs = useRef(new Map());
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const safeOptions = Array.isArray(options) ? options : [];
  const activeOption = useMemo(
    () => safeOptions.find((item) => item.value === value) || safeOptions[0],
    [safeOptions, value],
  );
  const activeMeta = showMeta ? formatOptionMeta(activeOption) : "";
  const ActiveIcon = activeOption?.icon || Square;
  const isDisabled = disabled || safeOptions.length === 0;

  useEffect(() => {
    if (isDisabled) {
      setOpen(false);
    }
  }, [isDisabled]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFocusedValue(value);
    window.requestAnimationFrame(() => {
      optionRefs.current.get(value)?.focus();
    });
  }, [open, value]);

  useLayoutEffect(() => {
    if (!open || isDisabled) {
      setMenuStyle(null);
      return undefined;
    }

    const updateMenuStyle = () => {
      const triggerNode = triggerRef.current;
      const menuNode = menuRef.current;
      if (!triggerNode || !menuNode) {
        return;
      }
      const rect = triggerNode.getBoundingClientRect();
      const gap = 8;
      const padding = 8;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const measuredHeight = menuNode.scrollHeight || menuNode.offsetHeight || 0;
      const spaceBelow = Math.max(0, viewportHeight - rect.bottom - gap - padding);
      const spaceAbove = Math.max(0, rect.top - gap - padding);
      const openUp = spaceBelow < measuredHeight && spaceAbove > spaceBelow;
      const availableSpace = openUp ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(0, Math.min(measuredHeight || 0, availableSpace));
      const left = Math.max(padding, Math.min(rect.left, viewportWidth - rect.width - padding));
      setMenuStyle({
        left: `${left}px`,
        width: `${rect.width}px`,
        top: openUp ? "auto" : `${rect.bottom + gap}px`,
        bottom: openUp ? `${Math.max(padding, viewportHeight - rect.top + gap)}px` : "auto",
        maxHeight: `${maxHeight}px`,
      });
    };

    updateMenuStyle();
    window.addEventListener("resize", updateMenuStyle);
    window.addEventListener("scroll", updateMenuStyle, true);
    return () => {
      window.removeEventListener("resize", updateMenuStyle);
      window.removeEventListener("scroll", updateMenuStyle, true);
    };
  }, [open, isDisabled, safeOptions, value]);

  function selectOption(nextValue) {
    if (isDisabled || !safeOptions.some((item) => item.value === nextValue)) {
      return;
    }
    onChange(nextValue);
    setOpen(false);
  }

  function moveFocus(direction) {
    const currentIndex = Math.max(0, safeOptions.findIndex((item) => item.value === focusedValue));
    const nextIndex = direction === "prev"
      ? Math.max(0, currentIndex - 1)
      : Math.min(safeOptions.length - 1, currentIndex + 1);
    const nextValue = safeOptions[nextIndex]?.value;
    if (!nextValue) {
      return;
    }
    setFocusedValue(nextValue);
    optionRefs.current.get(nextValue)?.focus();
  }

  function handleMenuKeyDown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus("next");
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus("prev");
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      const nextValue = safeOptions[0]?.value;
      setFocusedValue(nextValue);
      optionRefs.current.get(nextValue)?.focus();
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      const nextValue = safeOptions.at(-1)?.value;
      setFocusedValue(nextValue);
      optionRefs.current.get(nextValue)?.focus();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectOption(focusedValue);
    }
  }

  return (
    <label className={isDisabled ? "aspect-field is-disabled" : "aspect-field"} aria-disabled={isDisabled}>
      <span>{label}</span>
      <div className="aspect-select" onClick={(event) => event.stopPropagation()}>
        <button
          ref={triggerRef}
          type="button"
          className="aspect-trigger"
          aria-haspopup="listbox"
          aria-controls={`${id}-menu`}
          aria-expanded={open}
          disabled={isDisabled}
          onClick={() => setOpen((current) => !current)}
        >
          <ActiveIcon size={16} aria-hidden="true" />
          <span className="aspect-trigger-copy">
            <span className="aspect-trigger-text">{activeOption?.label || placeholder}</span>
            {showMeta && activeMeta && <span className="aspect-trigger-meta">{activeMeta}</span>}
          </span>
          <ChevronDown size={15} aria-hidden="true" />
        </button>
        {open && !isDisabled && typeof document !== "undefined"
          ? createPortal(
              <div
                className="aspect-menu"
                id={`${id}-menu`}
                ref={menuRef}
                role="listbox"
                aria-label={label}
                tabIndex={-1}
                style={menuStyle || { left: 0, top: 0, width: "auto", visibility: "hidden" }}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onKeyDown={handleMenuKeyDown}
              >
                {safeOptions.map((item) => {
                  const Icon = item.icon || Square;
                  const selected = item.value === value;
                  return (
                    <div
                      key={item.value}
                      ref={(node) => {
                        if (node) {
                          optionRefs.current.set(item.value, node);
                        } else {
                          optionRefs.current.delete(item.value);
                        }
                      }}
                      className={selected ? "aspect-option active" : "aspect-option"}
                      role="option"
                      tabIndex={focusedValue === item.value ? 0 : -1}
                      aria-selected={selected}
                      onClick={() => selectOption(item.value)}
                      onMouseEnter={() => setFocusedValue(item.value)}
                    >
                      <Icon size={16} aria-hidden="true" />
                      <span className="aspect-option-copy">
                        <span>{item.label}</span>
                        {showMeta && formatOptionMeta(item) && <em>{formatOptionMeta(item)}</em>}
                      </span>
                      {selected && <Check size={15} aria-hidden="true" />}
                    </div>
                  );
                })}
              </div>,
              document.body,
            )
          : null}
      </div>
    </label>
  );
}

function formatOptionMeta(option) {
  const parts = [option?.description, option?.meta].filter(Boolean);
  return parts.join(" · ");
}
